import { randomUUID } from '@betterment/core';
import { atom, useAtom, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { Notification, NotificationType } from './types';

const delayedNotificationsAtom = atomWithStorage<Notification[]>(
  'notifications',
  [],
  createJSONStorage(() => sessionStorage),
  {
    getOnInit: true,
  }
);

const immediateNotificationsAtom = atom<Notification[]>([]);

export type NotificationOptions = {
  isDismissable?: boolean;
  type: NotificationType;
  message: string;
};

const setNotificationAtom = atom(null, (get, set, { type, message, isDismissable = true }: NotificationOptions) => {
  try {
    set(delayedNotificationsAtom, [
      ...get(delayedNotificationsAtom),
      { id: randomUUID(), type, message, isDismissable },
    ]);
  } catch {
    set(immediateNotificationsAtom, [
      ...get(immediateNotificationsAtom),
      { id: randomUUID(), type, message, isDismissable },
    ]);
  }
});

const showNotificationAtom = atom(null, (get, set, { type, message, isDismissable = true }: NotificationOptions) => {
  set(immediateNotificationsAtom, [
    ...get(immediateNotificationsAtom),
    { id: randomUUID(), type, message, isDismissable },
  ]);
});

const flushNotificationsAtom = atom(null, (get, set) => {
  let delayed: Notification[] = [];
  try {
    delayed = get(delayedNotificationsAtom);
    set(delayedNotificationsAtom, []);
  } catch {
    // sessionStorage may be unavailable
  }
  set(immediateNotificationsAtom, [...get(immediateNotificationsAtom), ...delayed]);
});

const dismissNotificationAtom = atom(null, (get, set, id: string) => {
  set(
    immediateNotificationsAtom,
    get(immediateNotificationsAtom).filter(notification => notification.id !== id)
  );
});

interface UseNotificationsHookResult {
  showNotification: (notification: NotificationOptions) => void;
  setNotification: (notification: NotificationOptions) => void;
  dismissNotification: (id: string) => void;
  notifications: Notification[];
  flushNotifications: () => void;
}

export function useNotifications(): UseNotificationsHookResult {
  const [notifications] = useAtom(immediateNotificationsAtom);
  const setNotification = useSetAtom(setNotificationAtom);
  const showNotification = useSetAtom(showNotificationAtom);
  const flushNotifications = useSetAtom(flushNotificationsAtom);
  const dismissNotification = useSetAtom(dismissNotificationAtom);

  return { notifications, showNotification, setNotification, dismissNotification, flushNotifications };
}
